import { z } from 'zod';
import { emailChannelConfigSchema } from './channels.ts';
import { fieldsSchema } from './fields.ts';
import { DEFAULT_CAPTCHA_MIN_SCORE, LOCALE_CODES, captchaMinScoreSchema, captchaSiteKeySchema, settingsOverrideSchema } from './settings.ts';

// The declarative site file applied by `platform apply` (FR-2.2, spec §18 #43). It is the D0 way to
// configure a site; from R1 on the dashboard edits the same rows.

const slugSchema = z.string().regex(/^[a-z0-9][a-z0-9-]{0,62}$/u, 'must be lowercase letters, digits and dashes');

/** In a site file a CAPTCHA secret is named, not inlined: the value comes from an env var at apply time. */
const siteCaptchaKeys = { siteKey: captchaSiteKeySchema, secretEnv: z.string().regex(/^[A-Z][A-Z0-9_]*$/u) };
const siteCaptchaSchema = z.discriminatedUnion('provider', [
  z.object({ provider: z.literal('none') }),
  z.object({ provider: z.literal('turnstile'), ...siteCaptchaKeys }),
  z.object({ provider: z.literal('recaptcha_v2'), ...siteCaptchaKeys }),
  z.object({ provider: z.literal('recaptcha_v3'), ...siteCaptchaKeys, minScore: captchaMinScoreSchema.default(DEFAULT_CAPTCHA_MIN_SCORE) }),
]);

const siteSettingsSchema = settingsOverrideSchema.omit({ captcha: true }).extend({ captcha: siteCaptchaSchema.optional() }).strict();
export type SiteSettings = z.infer<typeof siteSettingsSchema>;

const siteFormSchema = z
  .object({
    slug: slugSchema,
    name: z.string().min(1).max(200),
    /** Pins the public form key (spec §18 #43) — used by smoke forms so probes know the key. */
    key: z.string().regex(/^[a-z0-9]{8,32}$/u).optional(),
    status: z.enum(['active', 'paused']).default('active'),
    settings: siteSettingsSchema.default({}),
    /** Channels bound at form level, in addition to the project's. */
    channels: z.array(slugSchema).default([]),
    /** Project-level channels this form switches off (FR-8.1). */
    disableChannels: z.array(slugSchema).default([]),
    fields: fieldsSchema,
  })
  .strict();

const siteProjectSchema = z
  .object({
    slug: slugSchema,
    name: z.string().min(1).max(200),
    settings: siteSettingsSchema.default({}),
    /** Channels bound at project level: every form inherits them. */
    channels: z.array(slugSchema).default([]),
    forms: z.array(siteFormSchema).default([]),
  })
  .strict();

const siteChannelSchema = z.discriminatedUnion('type', [
  z.object({ slug: slugSchema, name: z.string().min(1).max(200), type: z.literal('email'), config: emailChannelConfigSchema }).strict(),
]);

export const siteConfigSchema = z
  .object({
    tenant: z
      .object({
        slug: slugSchema,
        name: z.string().min(1).max(200),
        locale: z.enum(LOCALE_CODES).default('en'),
        timezone: z.string().min(1).max(64).default('UTC'),
        /** Must equal the cell's region; `apply` rejects anything else (FR-13.1). */
        region: z.string().min(2).max(16).optional(),
      })
      .strict(),
    channels: z.array(siteChannelSchema).default([]),
    projects: z.array(siteProjectSchema).default([]),
  })
  .strict();

export type SiteConfig = z.infer<typeof siteConfigSchema>;
export type SiteProject = SiteConfig['projects'][number];
export type SiteForm = SiteProject['forms'][number];
export type SiteChannel = SiteConfig['channels'][number];
